import { useEffect, useState } from "react";
import { w3cwebsocket as W3CWebSocket } from "websocket";
import { getIsOnpremise } from "../utils";
import getEnv from "../utils/getEnv";
var isOnpremise = getIsOnpremise();
var getWebsocketURL = function getWebsocketURL() {
  var _window;
  if (typeof window === "undefined") {
    return getEnv("NEXT_PUBLIC_WS_URL");
  }
  if (!isOnpremise) {
    return getEnv("NEXT_PUBLIC_WS_URL");
  }
  var hostname = (_window = window) === null || _window === void 0 ? void 0 : _window.location.hostname;
  return "ws://".concat(hostname, ":").concat(getEnv("NEXT_PUBLIC_ONPREMISE_BACKEND_PORT"), "/ws");
};
var useWebsocket = function useWebsocket(urlPath, isEnabled) {
  var _useState = useState(null),
    wsData = _useState[0],
    setWSData = _useState[1];
  var _useState2 = useState(null),
    socket = _useState2[0],
    setSocket = _useState2[1];
  // useful in showing the states for the socket connections
  var _useState3 = useState(false),
    isWSConnectionOpen = _useState3[0],
    setConnectionOpen = _useState3[1];
  var _useState4 = useState(true),
    isWSConnectionLoading = _useState4[0],
    setConnectionLoading = _useState4[1];
  // Increments on every successful (re)open. Consumers use it to resync state
  // that may have been pushed while the connection was down (events are not
  // replayed by the server), e.g. the worklist after a split-screen freeze.
  var _useState5 = useState(0),
    openCount = _useState5[0],
    setOpenCount = _useState5[1];
  useEffect(function () {
    if (!isEnabled) {
      return;
    }
    var websocket;
    var connectionTimeout;
    var isUnmounted = false;
    var _connect = function connect() {
      var socketUrl = getWebsocketURL() + urlPath;
      websocket = new W3CWebSocket(socketUrl);
      websocket.onopen = function () {
        setConnectionOpen(true);
        setConnectionLoading(false);
        clearTimeout(connectionTimeout);
        setSocket(websocket);
        setOpenCount(function (prev) {
          return prev + 1;
        });
      };
      websocket.onerror = function () {
        setConnectionOpen(false);
        setConnectionLoading(false);
        websocket.close();
      };
      websocket.onmessage = function (event) {
        if (typeof event.data === "string") {
          var data = JSON.parse(event.data);
          setWSData(data);
        }
      };
      websocket.onclose = function (e) {
        setConnectionOpen(false);
        if (!isUnmounted) {
          console.log("Socket is closed. Trying to reconnect...");
          connectionTimeout = setTimeout(function () {
            _connect();
          }, 5000);
        }
      };
    };

    // Reconnect immediately when the app regains focus/visibility/network and
    // the socket died meanwhile (Android split-screen/background freezes JS,
    // killing the connection and freezing the 5s reconnect timer with it).
    var reconnectIfDead = function reconnectIfDead() {
      if (isUnmounted || !websocket) return;
      var isAlive = websocket.readyState === W3CWebSocket.OPEN || websocket.readyState === W3CWebSocket.CONNECTING;
      if (!isAlive) {
        clearTimeout(connectionTimeout);
        _connect();
      }
    };
    var onVisibilityChange = function onVisibilityChange() {
      if (document.visibilityState === "visible") {
        reconnectIfDead();
      }
    };
    _connect();
    window.addEventListener("focus", reconnectIfDead);
    window.addEventListener("online", reconnectIfDead);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return function () {
      var _websocket;
      isUnmounted = true;
      clearTimeout(connectionTimeout);
      window.removeEventListener("focus", reconnectIfDead);
      window.removeEventListener("online", reconnectIfDead);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (((_websocket = websocket) === null || _websocket === void 0 ? void 0 : _websocket.readyState) === 1) {
        var _websocket2;
        setConnectionOpen(false);
        (_websocket2 = websocket) === null || _websocket2 === void 0 || _websocket2.close();
      }
    };
  }, []);
  return {
    socket: socket,
    wsData: wsData,
    isWSConnectionOpen: isWSConnectionOpen,
    isWSConnectionLoading: isWSConnectionLoading,
    openCount: openCount
  };
};
export default useWebsocket;